import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, of, timer } from 'rxjs';
import { catchError, switchMap, takeWhile } from 'rxjs/operators';
import { ApiService } from 'src/app/core/services/api.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { NotificationService } from 'src/app/core/services/notification.service';

const SESSION_KEY = 'saleOrderExportJobId';
const POLL_MS = 2000;
const MAX_POLLS = 450; // ~15 minutes

export type ExportJobStatus =
    | 'idle'
    | 'pending'
    | 'processing'
    | 'ready'
    | 'failed'
    | 'cancelled';

export interface ExportProgressState {
    jobId: number | null;
    status: ExportJobStatus;
    message: string;
    errorMessage?: string | null;
    inProgress: boolean;
}

@Injectable({ providedIn: 'root' })
export class SaleOrderExportPollerService extends ApiService implements OnDestroy {
    private readonly stateSubject = new BehaviorSubject<ExportProgressState>({
        jobId: null,
        status: 'idle',
        message: '',
        errorMessage: null,
        inProgress: false,
    });

    readonly state$ = this.stateSubject.asObservable();

    private pollSub: Subscription;
    private pollCount = 0;
    private resumedFromSession = false;

    constructor(
        public http: HttpClient,
        public storageBrowser: LocalStorageService,
        private notify: NotificationService,
    ) {
        super(http, storageBrowser);
        const stored = sessionStorage.getItem(SESSION_KEY);
        if (stored) {
            const jobId = Number(stored);
            if (Number.isFinite(jobId) && jobId > 0) {
                this.resumedFromSession = true;
                this.startPolling(jobId);
            }
        }
    }

    get isExportInProgress(): boolean {
        return this.stateSubject.value.inProgress;
    }

    get currentJobId(): number | null {
        return this.stateSubject.value.jobId;
    }

    /** Call before opening the progress dialog so a previous `ready` state does not auto-close it. */
    prepareNewExport(): void {
        this.stopPolling();
        sessionStorage.removeItem(SESSION_KEY);
        this.setState({
            jobId: null,
            status: 'pending',
            message: 'Starting export…',
            errorMessage: null,
            inProgress: true,
        });
    }

    startExportAsync(query: any, body: any): Observable<{ jobId: number; status: string }> {
        return this.request({
            path: 'sale-orders/export-async',
            method: 'POST',
            query,
            body: body || {},
        });
    }

    getExportJob(jobId: number): Observable<any> {
        return this.request({
            path: `sale-orders/export-jobs/${jobId}`,
            method: 'GET',
        });
    }

    cancelExportJob(jobId: number): Observable<any> {
        return this.request({
            path: `sale-orders/export-jobs/${jobId}/cancel`,
            method: 'POST',
            body: {},
        });
    }

    startJobAndTrack(start$: Observable<{ jobId: number; status: string }>): void {
        if (this.isExportInProgress && this.currentJobId) {
            return;
        }

        // Ensure UI is not stuck on a previous job's `ready` status.
        if (!this.isExportInProgress) {
            this.prepareNewExport();
        }

        start$.subscribe({
            next: (res) => {
                const jobId = Number(res?.jobId);
                if (!Number.isFinite(jobId) || jobId <= 0) {
                    this.setState({
                        jobId: null,
                        status: 'failed',
                        message: 'Failed to start export.',
                        errorMessage: 'Invalid export job id',
                        inProgress: false,
                    });
                    return;
                }
                this.startPolling(jobId);
            },
            error: (err) => {
                const apiMessage =
                    err?.error?.message
                    || (typeof err?.error === 'string' ? err.error : null)
                    || err?.message
                    || 'Request failed';
                this.setState({
                    jobId: null,
                    status: 'failed',
                    message: 'Export could not be started.',
                    errorMessage: apiMessage,
                    inProgress: false,
                });
                sessionStorage.removeItem(SESSION_KEY);
            },
        });
    }

    startPolling(jobId: number): void {
        this.stopPolling();
        this.pollCount = 0;
        sessionStorage.setItem(SESSION_KEY, String(jobId));
        this.setState({
            jobId,
            status: 'pending',
            message: 'Export started. Your file will download when ready.',
            errorMessage: null,
            inProgress: true,
        });

        this.pollSub = timer(0, POLL_MS)
            .pipe(
                takeWhile(() => this.stateSubject.value.inProgress && this.pollCount < MAX_POLLS),
                switchMap(() => {
                    this.pollCount += 1;
                    if (this.pollCount >= MAX_POLLS) {
                        return of({
                            jobId,
                            status: 'failed',
                            errorMessage: 'Export timed out. Please try again.',
                        });
                    }
                    return this.getExportJob(jobId).pipe(
                        catchError((err) => of({
                            jobId,
                            status: 'failed',
                            errorMessage: err?.error?.message || err?.message || 'Failed to check export status',
                        })),
                    );
                }),
            )
            .subscribe((res: any) => {
                const status = (res?.status || 'failed') as ExportJobStatus;

                if (status === 'pending' || status === 'processing') {
                    this.setState({
                        jobId,
                        status,
                        message: 'Export started. Your file will download when ready.',
                        errorMessage: null,
                        inProgress: true,
                    });
                    return;
                }

                if (status === 'ready') {
                    this.setState({
                        jobId,
                        status: 'ready',
                        message: 'Export ready — downloading…',
                        errorMessage: null,
                        inProgress: false,
                    });
                    sessionStorage.removeItem(SESSION_KEY);
                    this.stopPolling();
                    this.downloadExportFile(jobId);
                    return;
                }

                if (status === 'cancelled') {
                    this.setState({
                        jobId,
                        status: 'cancelled',
                        message: 'Export was cancelled.',
                        errorMessage: res?.errorMessage || null,
                        inProgress: false,
                    });
                    sessionStorage.removeItem(SESSION_KEY);
                    this.stopPolling();
                    return;
                }

                this.setState({
                    jobId,
                    status: 'failed',
                    message: 'Export failed.',
                    errorMessage: res?.errorMessage || 'Failed to generate Excel file',
                    inProgress: false,
                });
                sessionStorage.removeItem(SESSION_KEY);
                this.stopPolling();
            });
    }

    cancelCurrent(): Observable<any> {
        const jobId = this.currentJobId;
        this.stopPolling();
        sessionStorage.removeItem(SESSION_KEY);
        this.setState({
            jobId,
            status: 'cancelled',
            message: 'Export was cancelled.',
            errorMessage: null,
            inProgress: false,
        });
        if (!jobId) {
            return of(null);
        }
        return this.cancelExportJob(jobId).pipe(
            catchError(() => of(null)),
        );
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }

    private stopPolling(): void {
        if (this.pollSub) {
            this.pollSub.unsubscribe();
            this.pollSub = null;
        }
    }

    private setState(state: ExportProgressState): void {
        this.stateSubject.next(state);
    }

    private downloadExportFile(jobId: number): void {
        this.requestFile({
            path: `sale-orders/export-jobs/${jobId}/file`,
            method: 'GET',
        }, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet').subscribe({
            next: (blob) => {
                if (!blob || (blob as Blob).size === 0) {
                    this.setState({
                        jobId,
                        status: 'failed',
                        message: 'Export failed.',
                        errorMessage: 'Downloaded file was empty. Please try again.',
                        inProgress: false,
                    });
                    return;
                }
                const a = document.createElement('a');
                const objectUrl = URL.createObjectURL(blob);
                a.href = objectUrl;
                a.download = `Sale-Orders${new Date().toDateString()}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(objectUrl);
                if (this.resumedFromSession) {
                    this.notify.showSuccessMessage('Export ready — downloading…');
                    this.resumedFromSession = false;
                }
            },
            error: (err) => {
                this.setState({
                    jobId,
                    status: 'failed',
                    message: 'Export failed.',
                    errorMessage: err?.error?.message || err?.message || 'Failed to download export file',
                    inProgress: false,
                });
            },
        });
    }
}
